import type { UseMutateFunction } from '@tanstack/react-query';
import { Button, message } from 'antd';
import { useForm, SubmitHandler } from 'react-hook-form';

import { RegisterFormInputs } from 'types';

import type { RegisterUserResponse } from '../../services/auth';
import { Input } from '../common/input';
import useTranslation from '../../customHooks/translate';

interface IProps {
  registerHandler: UseMutateFunction<RegisterUserResponse, any, any>;
  isLoading: boolean;
  emailError?: string | null;
  passwordError?: string | null;
}

const RegisterForm = ({
  registerHandler,
  isLoading,
  passwordError,
  emailError,
}: IProps) => {
  const translation = useTranslation();
  const { register, handleSubmit, formState } = useForm<RegisterFormInputs>({
    defaultValues: {
      packageId: 1,
    },
  });
  const { errors } = formState;

  const onSubmit: SubmitHandler<RegisterFormInputs> = (body) => {
    if (!body.password || !body.email) {
      message.error(translation.login.emptyFields);
      return;
    }
    registerHandler(body);
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-[24px]"
    >
      <div className="flex flex-col gap-[8px]">
        <Input
          id="email"
          label={translation.register.Mail}
          placeholder="example-mail@domain.com"
          error={errors.email}
          autoComplete="email"
          {...register('email', {
            required: translation.login.requiredField,
          })}
          style={{
            borderColor: errors.email || emailError ? 'red' : '',
          }}
        />

        {errors.email && (
          <span
            className="animate__animated animate__shakeX"
            style={{ color: 'red', fontSize: '12px' }}
          >
            {errors.email.message}
          </span>
        )}
        {emailError && (
          <span
            className="animate__animated animate__shakeX"
            style={{ color: 'red', fontSize: '12px' }}
          >
            {emailError}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-[8px]">
        <Input
          id="password"
          label={translation.register.SetPassword}
          placeholder="• • • • • • • •"
          error={errors.password}
          type="password"
          autoComplete="new-password"
          style={{
            borderColor: errors.password || passwordError ? 'red' : '',
          }}
          {...register('password', {
            required: translation.login.requiredField,
          })}
        />

        {errors.password && (
          <span
            className="animate__animated animate__shakeX"
            style={{ color: 'red', fontSize: '12px' }}
          >
            {errors.password.message}
          </span>
        )}
        {passwordError && (
          <span
            className="animate__animated animate__shakeX"
            style={{ color: 'red', fontSize: '12px' }}
          >
            {passwordError}
          </span>
        )}
      </div>

      <Button
        htmlType="submit"
        className="h-[44px] rounded-md !border-0 !bg-[#6DCE92] font-medium !text-white shadow-none transition-colors duration-200 hover:!border-0 hover:!bg-[#5EC082] hover:!text-white focus:!border-0 focus:!bg-[#6DCE92] focus:!text-white active:!border-0 active:!bg-[#5EC082] active:!text-white"
        loading={isLoading}
      >
        {translation.register.SignUpButton}
      </Button>
    </form>
  );
};

export default RegisterForm;
